import { inject } from "@angular/core";
import { Router, Routes } from "@angular/router";
import { EnvironmentGuard } from "./guards/environment.guard";
import { PendingChangesGuard } from "./guards/pending-changes.guard";
import { RegionGuard } from "./guards/region.guard";
import { UnsavedTimeSheetGuard } from "./guards/unsaved-timesheet.guard";
import { CONSTANTS } from "./models/constants";
import { DashboardComponent } from "./routes/dashboard/dashboard.component";
import { ErrorPageComponent } from "./routes/error-page/error-page.component";

const homeRoutes: Routes = [
  {
    path: "",
    canActivate: [],
    children: [
      {
        path: "",
        loadComponent: () =>
          import("./routes/dashboard/dashboard.component").then((m) => m.DashboardComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.HOME}`,
      },
      {
        path: "staffing",
        loadComponent: () =>
          import("./routes/staffing/staffing.component").then((m) => m.StaffingComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.STAFFING}`,
      },
      {
        path: "roles",
        loadComponent: () => import("./routes/roles/roles.component").then((m) => m.RolesComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.ROLES}`,
      },
      {
        path: "my-work/:tab",
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_WORK}`,
        loadComponent: () =>
          import("./routes/my-work/my-work.component").then((m) => m.MyWorkComponent),
        canActivate: [RegionGuard],
        canDeactivate: [UnsavedTimeSheetGuard],
      },
      {
        path: "my-work",
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_WORK}`,
        loadComponent: () =>
          import("./routes/my-work/my-work.component").then((m) => m.MyWorkComponent),
        canActivate: [RegionGuard],
      },
      {
        path: "roles/staffing -opportunities",
        redirectTo: "/staffing",
      },
      {
        path: "roles/library",
        loadComponent: () =>
          import("./routes/role-library/role-library.component").then(
            (m) => m.RoleLibraryComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.ROLE_LIBRARY}`,
      },
      {
        path: "staffing/staffing-opportunities",
        loadComponent: () =>
          import("./routes/staffing-opportunities/staffing-opportunities.component").then(
            (m) => m.StaffingOpportunitiesComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.STAFFING_OPPORTUNITIES}`,
      },
      {
        path: "staffing/staffing-opportunities/:staffingRequestId",
        loadComponent: () =>
          import(
            "./routes/staffing-opportunity-detail-page/staffing-opportunity-detail-page.component"
          ).then((m) => m.StaffingOpportunityDetailPageComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.STAFFING_OPPORTUNITIES}`,
      },
      {
        path: "roles/add",
        redirectTo: () =>
          inject(Router).createUrlTree(["/roles/library"], {
            queryParams: { filter: "unassigned" },
          }),
        pathMatch: "full",
      },
      {
        path: "roles/add/:id",
        loadComponent: () =>
          import("./routes/my-roles/my-roles-add2/my-roles-add2.component").then(
            (m) => m.MyRolesAdd2Component
          ),
        canDeactivate: [PendingChangesGuard],
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.ROLES}`,
      },
      {
        path: "roles/library/:id",
        loadComponent: () =>
          import("./routes/role-library/role-library-details/role-library-details.component").then(
            (m) => m.RoleLibraryDetailsComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.ROLE_LIBRARY}`,
      },
      {
        path: "roles/library/:id/mastery",
        loadComponent: () =>
          import("./routes/role-library/role-library-mastery/role-library-mastery.component").then(
            (m) => m.RoleLibraryMasteryComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.ROLE_LIBRARY}`,
      },
      {
        path: "roles/:type",
        loadComponent: () => import("./routes/roles/roles.component").then((m) => m.RolesComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.ROLES}`,
      },
      {
        path: "roles/my-roles/:roleid",
        loadComponent: () =>
          import(
            "./routes/employee-team/employee-role-details/employee-role-details.component"
          ).then((m) => m.EmployeeRoleDetailsComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_ROLES}`,
      },
      {
        path: "my-roles/:roleid", // temporarily adding for role rejection email backwards compatibility
        loadComponent: () =>
          import(
            "./routes/employee-team/employee-role-details/employee-role-details.component"
          ).then((m) => m.EmployeeRoleDetailsComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_ROLES}`,
      },
      {
        path: "roles/my-roles/edit/:id",
        loadComponent: () =>
          import("./routes/my-roles/my-roles-add2/my-roles-add2.component").then(
            (m) => m.MyRolesAdd2Component
          ),
        canDeactivate: [PendingChangesGuard],
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_ROLES}`,
      },
      {
        path: "titles",
        loadComponent: () =>
          import("./routes/job-title-library/job-title-library.component").then(
            (m) => m.JobTitleLibraryComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.JOB_TITLE_LIBRARY_TRACKS}`,
      },
      {
        path: "titles/resources/:resource",
        loadComponent: () =>
          import("./routes/job-title-library/job-title-library.component").then(
            (m) => m.JobTitleLibraryComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.JOB_TITLE_LIBRARY_TRACKS}`,
      },
      {
        path: "titles/:id",
        loadComponent: () =>
          import("./routes/job-title-library/job-title-details/job-title-details.component").then(
            (m) => m.JobTitleDetailsComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.JOB_TITLE_LIBRARY_TRACKS}`,
      },
      {
        path: "roles/learning/resources/role/:roleId",
        loadComponent: () =>
          import("./routes/role-library/role-learning/role-learning.component").then(
            (m) => m.RoleLearningComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.ROLES}`,
      },
      {
        path: "learning",
        loadComponent: () =>
          import("./routes/learning/learning.component").then((m) => m.LearningComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.LEARNING}`,
      },
      {
        path: "learning/learning-catalog",
        loadComponent: () =>
          import("./routes/learning/all-learning/all-learning.component").then(
            (m) => m.AllLearningComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.LEARNING}`,
      },
      {
        path: "learning/:pathId",
        loadComponent: () =>
          import("./routes/learning/learning.component").then((m) => m.LearningComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.LEARNING}`,
      },
      {
        path: "learning/path/:id",
        loadComponent: () =>
          import("./routes/learning/learning-path/learning-path.component").then(
            (m) => m.LearningPathComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.LEARNING}`,
      },
      {
        path: "search",
        loadComponent: () =>
          import("./search/advanced-search/advanced-search.component").then(
            (m) => m.AdvancedSearchComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.SEARCH}`,
      },
      {
        path: "advanced-search",
        loadComponent: () =>
          import("./search/advanced-search/advanced-search.component").then(
            (m) => m.AdvancedSearchComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.SEARCH}`,
      },
      {
        path: "my-profile",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_PROFILE}`,
      },
      {
        path: "my-profile/:section",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_PROFILE}`,
      },
      {
        path: "my-profile/:id/consultantProfiles",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
      },
      {
        path: "my-profile/:id/consultantProfiles/:sourceSystemId",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
      },
      {
        path: "profile/:id",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
      },
      {
        path: "profile/:id/workHistory",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
      },
      {
        path: "profile/:id/team",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
      },
      {
        path: "profile/:id/consultantProfiles",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
      },
      {
        path: "profile/:id/consultantProfiles/:sourceSystemId",
        loadComponent: () =>
          import("./routes/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
      },
      {
        path: "team",
        loadComponent: () =>
          import("./routes/employee-team/employee-team.component").then(
            (m) => m.EmployeeTeamComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_TEAM}`,
      },
      {
        path: "team/:id",
        loadComponent: () =>
          import("./routes/employee-team/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_TEAM}`,
      },
      {
        path: "team/:id/reports",

        loadComponent: () =>
          import("./routes/employee-team/employee-team.component").then(
            (m) => m.EmployeeTeamComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_TEAM}`,
      },
      {
        path: "team/:id/role/:roleid",
        loadComponent: () =>
          import(
            "./routes/employee-team/employee-role-validation/employee-role-validation.component"
          ).then((m) => m.EmployeeRoleValidationComponent),
        canDeactivate: [PendingChangesGuard],
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_TEAM}`,
      },
      {
        path: "team/:id/role/:roleid/details",
        loadComponent: () =>
          import(
            "./routes/employee-team/employee-role-details/employee-role-details.component"
          ).then((m) => m.EmployeeRoleDetailsComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_TEAM}`,
      },
      {
        path: "team/:id/reflection",
        loadComponent: () =>
          import("./routes/employee-team/reflection-review/reflection-review.component").then(
            (m) => m.ReflectionReviewComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_TEAM}`,
      },
      {
        path: "team/:id/:tab",
        loadComponent: () =>
          import("./routes/employee-team/employee-profile/employee-profile.component").then(
            (m) => m.EmployeeProfileComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.MY_TEAM}`,
      },
      {
        path: "career-navigator/consulting",
        loadComponent: () =>
          import("./routes/career-navigator/consultant-org/consultant-org.component").then(
            (m) => m.ConsultantOrgComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.CAREER_NAVIGATOR}`,
      },
      {
        path: "career-navigator/consulting/:track",
        loadComponent: () =>
          import("./routes/career-navigator/consultant-track/consultant-track.component").then(
            (m) => m.ConsultantTrackComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.CAREER_NAVIGATOR}`,
      },
      {
        path: "career-navigator/roles",
        loadComponent: () =>
          import(
            "./routes/role-library/role-library-container/role-library-container.component"
          ).then((m) => m.RoleLibraryContainerComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.ROLE_LIBRARY}`,
      },
      {
        path: "career-navigator/:type/:id/:titleId/:section",
        loadComponent: () =>
          import("./routes/career-navigator/career-navigator.component").then(
            (m) => m.CareerNavigatorComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.CAREER_NAVIGATOR}`,
      },

      {
        path: "career-navigator/:type/:id/:titleId",
        loadComponent: () =>
          import("./routes/career-navigator/career-navigator.component").then(
            (m) => m.CareerNavigatorComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.CAREER_NAVIGATOR}`,
      },

      {
        path: "career-navigator/resources/:resource",
        loadComponent: () =>
          import("./routes/job-title-library/job-title-library.component").then(
            (m) => m.JobTitleLibraryComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.CAREER_NAVIGATOR}`,
      },
      {
        path: "career-navigator/:type/:id",
        loadComponent: () =>
          import("./routes/career-navigator/career-navigator.component").then(
            (m) => m.CareerNavigatorComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.CAREER_NAVIGATOR}`,
      },
      {
        path: "career-navigator",
        loadComponent: () =>
          import("./routes/career-navigator/career-navigator.component").then(
            (m) => m.CareerNavigatorComponent
          ),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.CAREER_NAVIGATOR}`,
      },
      {
        path: "profile-decks",
        loadComponent: () =>
          import("./routes/profile-decks/profile-decks.component").then(
            (m) => m.ProfileDecksComponent
          ),
        canActivate: [EnvironmentGuard],
        data: { envVariable: "showProfileDecks" },
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.PROFILE_DECKS}`,
      },
      {
        path: "profile-deck",
        redirectTo: "/profile-decks",
        pathMatch: "full",
      },
      {
        path: "profile-deck/:id",
        loadComponent: () =>
          import("./routes/profile-deck-detail/profile-deck-detail.component").then(
            (m) => m.ProfileDeckDetailComponent
          ),
        canActivate: [EnvironmentGuard],
        data: { envVariable: "showProfileDecks" },
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.PROFILE_DECKS}`,
      },
      {
        path: "insights",
        loadComponent: () =>
          import("./routes/insights/insights.component").then((m) => m.InsightsComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.INSIGHTS}`,
      },
      {
        path: "insights/:metric",
        loadComponent: () =>
          import("./routes/insights/insights.component").then((m) => m.InsightsComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.INSIGHTS}`,
      },
      {
        path: "insights/:metric/:level",
        loadComponent: () =>
          import("./routes/insights/insights.component").then((m) => m.InsightsComponent),
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.INSIGHTS}`,
      },

      {
        path: "notifications",
        loadComponent: () =>
          import("./header/notification-menu/notifications/notifications.component").then(
            (m) => m.NotificationsComponent
          ),
        canActivate: [EnvironmentGuard],
        data: { envVariable: "showNotifications" },
        title: `${CONSTANTS.APP_TITLE} - ${CONSTANTS.NOTIFICATIONS}`,
      },
      {
        path: "home",
        redirectTo: "/",
        pathMatch: "full",
      },
      {
        path: "index",
        redirectTo: "/",
        pathMatch: "full",
      },
      {
        path: "404",
        redirectTo: "error/404",
      },
      {
        path: "403",
        redirectTo: "error/403",
      },
      {
        path: "error/:error",
        component: ErrorPageComponent,
      },
    ],
  },
];

export const routes: Routes = [
  {
    path: "",
    children: [
      ...homeRoutes,
      {
        path: "",
        component: DashboardComponent,
      },
      {
        path: "**",
        redirectTo: "error/404",
      },
    ],
  },
];
